import React from 'react';
import { actions, feedbackV2 } from '@ecosystem/newron-design-system';
import './quicklook.css';
import { DataRow, Params } from '../../types';
import { ContentElement } from '../../content/element';
import { QuickLookOptions } from './types';
import classNames from 'classnames';
import { useMobile } from '../../context/mobile/mobile.provider';

const { Alert } = feedbackV2;

type Props = {
  options: QuickLookOptions;
  rowId?: string;
  row?: DataRow;
  ids: string[];
  onSelect: (rowId: string) => void;
  onClose: () => void;
  updateSettings: (updates: Params) => void;
  paramsForData: Params;
  dataUrlRoot: string;
  componentSettings: Params;
  isDisabled: boolean;
};
function QuickLook({
  options,
  rowId,
  row,
  ids,
  onSelect,
  onClose,
  dataUrlRoot,
  paramsForData,
  updateSettings,
  componentSettings,
  isDisabled,
}: Props) {
  const { titleField, preTitleField, contents } = options;

  const expanded = rowId !== undefined;

  const { isMobile } = useMobile();

  let canMoveUp = false;
  let canMoveDown = false;
  if (expanded && ids) {
    canMoveUp = ids.indexOf(rowId) > 0;
    canMoveDown = ids.indexOf(rowId) < ids.length - 1;
  }

  let preTitleText: string | undefined;
  let titleText: string | undefined;
  if (row) {
    titleText = String(row.values[titleField]);
    preTitleText = preTitleField ? String(row.values[preTitleField]) : undefined;
  }

  const handleMoveUp = () => {
    const newId = ids[ids.indexOf(rowId) - 1];
    newId && onSelect(newId);
  };

  const handleMoveDown = () => {
    const newId = ids[ids.indexOf(rowId) + 1];
    newId && onSelect(newId);
  };

  const { IconButton } = actions;

  return (
    <div
      className={classNames('quicklook fixed top-0 right-0 bottom-0 bg-white transition-width', {
        'quicklook--expanded border-l border-light shadow-lg': expanded,
        'opacity-0': !expanded,
        'mobile-w-98vw': isMobile,
      })}
    >
      <div
        className={`quicklook-inner absolute top-0 left-0 right-0 bottom-0 ${expanded ? 'opacity-100' : 'opacity-0'}`}
      >
        {expanded && (
          <div className="relative flex-column pt-4 px-3 h-full">
            <div className="flex items-center gap-1 py-2">
              <span className="rounded border border-light">
                <IconButton onClick={handleMoveUp} disabled={!canMoveUp}>
                  <IconButton.icons.ArrowUpward />
                </IconButton>
              </span>
              <span className="rounded border border-light">
                <IconButton onClick={handleMoveDown} disabled={!canMoveDown}>
                  <IconButton.icons.ArrowDownward />
                </IconButton>
              </span>
              <span className="flex-grow" />
              <IconButton onClick={onClose}>
                <IconButton.icons.Close />
              </IconButton>
            </div>
            <div className="flex-grow overflow-y-auto overscroll-contain px-3 mb-6">
              {preTitleText && <div className="text-heading text-lg">{preTitleText}</div>}
              {titleText && <div className="text-heading text-lg font-bold mb-4">{titleText}</div>}
              {isDisabled ? (
                <Alert title="No further data available" type="info" />
              ) : (
                <div className="flex flex-column gap-4 mb-8">
                  {contents.map((content, idx) => (
                    <ContentElement
                      hideElement={false}
                      setContentElementHasData={() => {}}
                      key={`${content.type}_${idx}`}
                      contentElementId={`${content.type}_${idx}`}
                      content={content}
                      paramsForData={{
                        ...paramsForData,
                        id: [rowId],
                        cell: (row.values.cells as string)?.split(',') ?? paramsForData.cell,
                      }}
                      dataUrlRoot={dataUrlRoot}
                      updateComponentSettings={updateSettings}
                      componentSettings={componentSettings}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export { QuickLook };
